import { capitalize } from '@attain-apps/string-utils'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as _selectors from '../selectors'

import * as _config from './config'
import type { Feature } from './feature'

export type Selectors<
  TPropertiesConfig extends _config.PropertiesConfig.Constraint,
> = {
  /**
   * Selectors for the desired output value of each property
   */
  [TProp in keyof TPropertiesConfig]: TPropertiesConfig[TProp] extends _config.PropertyConfig.Constraint<
    any,
    infer TOut
  >
    ? _selectors.BoundSelector<TOut>
    : TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint<
          infer TKey,
          any,
          any,
          infer TStored
        >
      ? _selectors.BoundSelector<Record<TKey, TStored | undefined>>
      : never
} & {
  [TProp in keyof TPropertiesConfig as TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint
    ? `getFrom${Capitalize<Extract<TProp, string>>}`
    : never]: TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint<
    infer TKey,
    any,
    infer TOut
  >
    ? ReturnType<typeof _createSelectorMethod<[TKey], TOut>>
    : never
} & {
  [TProp in keyof TPropertiesConfig as TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint
    ? `${Extract<TProp, string>}Keys`
    : never]: TPropertiesConfig[TProp] extends _config.LookupConfig.Constraint<
    infer TKey
  >
    ? _selectors.BoundSelector<readonly TKey[]>
    : never
}

export function createSelectors<
  TConfig extends _config.PropertiesConfig.Constraint,
>(feature: Feature.Constraint, config: TConfig): Selectors<TConfig>
export function createSelectors(
  feature: Feature.Constraint,
  config: _config.PropertiesConfig.Default,
): Selectors<_config.PropertiesConfig.Constraint> {
  const values = feature.state.project(state => state.values)

  // Since properties can have any name, and selector names are based on
  // property names, we end up with just a record of selectors or selector
  // methods
  const selectors: Record<
    string,
    | _selectors.BoundSelector<unknown>
    | ReturnType<typeof _createSelectorMethod<readonly any[], unknown>>
  > = {}
  for (const prop in config) {
    const propConfig = config[prop]

    switch (propConfig[_config.Marker.SYMBOL]) {
      case _config.Marker.Type.Property: {
        let { get } = propConfig
        get = get?.bind(propConfig)
        // Basic selector for the value itself
        selectors[prop] = values.project(_values => {
          const value = _values[prop]
          // If there is a getter, we need to use it to get the output.
          // Otherwise, we can just return the stored value
          return get ? get(value) : value
        })
        break
      }
      case _config.Marker.Type.Lookup: {
        let { get } = propConfig
        get = get?.bind(propConfig)
        // Basic selector for the lookup itself
        const lookupSelector = (selectors[prop] = values.project(
          _values => _values[prop] ?? {},
        ))

        // For lookups, we add a selector method for getting individual values
        // (based on a given key) from the lookup
        selectors[`getFrom${capitalize(prop)}`] = feature.createSelectorMethod(
          (key: string | undefined = propConfig.defaultKey) =>
            key === undefined
              ? _selectors.Selector.always(undefined)
              : lookupSelector.project(lookup => {
                  const stored = (lookup as Unknown)?.[key]
                  return get ? get(key, stored) : stored
                }).asSelector,
        )

        // We also add a selector to get the list of keys
        selectors[`${prop}Keys`] = lookupSelector.project(lookup =>
          lookup ? Object.keys(lookup) : [],
        )
        break
      }
      default:
        throw TypeAsserter.assertIsNever(propConfig[_config.Marker.SYMBOL])
    }
  }
  // NOTE: since we don't know what properties are going to be given, and the
  // return type is entirely based on the properties that are given, we couldn't
  // find a way to get TypeScript to understand the
  return selectors as Selectors<_config.PropertiesConfig.Constraint>
}

/**
 * This is just used to allow us to pass type parameters above
 *
 * TODO: give this method a standard return type so we don't have to do this
 * weird trick
 */
declare const _createSelectorMethod: Feature<any>['createSelectorMethod']
